// @ts-nocheck
// Decision page — variant B of the landing experiment (Shlomo, 29.8.2026).
//
// The Clarity finding: the median ad visitor spends ~26 seconds and the
// free-chapter funnel asks for a 25-minute task before any decision.
// Variant B strips everything except the strongest asset — the reader-voice
// quotes + Shlomo's answer + the promise — then goes straight to the offer
// (the exact PaywallGate card already live on the paid chapters). One quiet
// escape link to the free chapter keeps the ads' "המפגש הראשון בחינם"
// promise honest.
//
// Route: /b · traffic split at the AD level (duplicated ad, its own utm).
// The A page (/) is untouched.

import { useEffect } from "react";
import { PaywallGate } from "./PaywallGate";
import { logSiteEvent } from "../lib/siteEvents";

const styles = `
.dp-wrap { max-width: 720px; margin: 0 auto; padding: 26px 18px 20px; direction: rtl; font-family: 'Heebo', sans-serif; }
.dp-brand { text-align: center; font-family: 'Frank Ruhl Libre', serif; font-size: 21px; font-weight: 700; color: #A0704A; margin-bottom: 4px; }
.dp-brand small { display: block; font-family: 'Heebo', sans-serif; font-size: 12.5px; font-weight: 400; color: #8B7355; margin-top: 2px; }
.dp-quotes { background: #FFFBF5; border-right: 4px solid #D4875A; border-radius: 0 12px 12px 0; padding: 18px 22px; margin: 22px 0 20px; font-style: italic; color: #5C3D2E; font-size: 15.5px; line-height: 1.9; }
.dp-quotes p { margin: 0 0 12px; }
.dp-quotes p:last-child { margin-bottom: 0; }
.dp-body { color: #2D1B10; font-size: 16px; line-height: 1.9; }
.dp-body p { margin: 0 0 16px; }
.dp-invite { background: rgba(212,135,90,0.09); border: 1px solid rgba(196,168,130,0.35); border-radius: 12px; padding: 12px 16px; font-weight: 500; }
.dp-vector { text-align: center; margin: 18px 0 6px; }
.dp-vector h1 { font-family: 'Frank Ruhl Libre', serif; font-size: 26px; font-weight: 700; color: #2D1B10; line-height: 1.45; margin: 0 0 10px; }
.dp-vector p { color: #5C3D2E; font-size: 15.5px; line-height: 1.8; max-width: 560px; margin: 0 auto 6px; }
.dp-cta { display: inline-block; background: linear-gradient(135deg, #A0704A, #D4875A); color: #fff; border: none; border-radius: 16px; padding: 13px 30px; font-family: 'Heebo', sans-serif; font-size: 16.5px; font-weight: 700; cursor: pointer; box-shadow: 0 6px 20px rgba(160,112,74,0.35); margin-top: 10px; }
.dp-cta small { display: block; font-size: 12px; font-weight: 400; opacity: 0.9; margin-top: 2px; }
.dp-ribbon { display: block; text-decoration: none; background: linear-gradient(90deg, #1E2A44, #34507A); color: #fff; border-bottom: 3px solid #F0C27B; text-align: center; padding: 16px 14px 14px; line-height: 1.3; margin: -26px -18px 18px; }
.dp-ribbon b { display: block; color: #F0C27B; font-family: 'Frank Ruhl Libre', serif; font-size: 44px; font-weight: 700; line-height: 1.15; }
.dp-ribbon span { display: block; font-size: 19px; font-weight: 600; margin-top: 6px; }
@media (max-width: 640px) { .dp-ribbon b { font-size: 34px; } .dp-ribbon span { font-size: 16px; } }
.dp-gift-hero { text-align: center; margin: 10px 0 6px; }
.dp-gift-hero .dp-kicker { font-size: 13px; letter-spacing: 1px; color: #34507A; font-weight: 700; }
.dp-gift-hero h1 { font-family: 'Frank Ruhl Libre', serif; font-size: 28px; font-weight: 700; color: #2D1B10; line-height: 1.4; margin: 6px 0 10px; }
.dp-gift-hero p { color: #5C3D2E; font-size: 15.5px; line-height: 1.8; max-width: 560px; margin: 0 auto; }
`;

// gift=true renders the /gift landing: a short gift header, then the same
// offer card already opened on the gift form (no skeptic vector, no quotes -
// whoever arrives here came from the holiday post and knows what they want).
export function DecisionPage({ gift = false }: { gift?: boolean }) {
  // Funnel measurement (6.10.2026): one view event per page load.
  useEffect(() => {
    logSiteEvent(gift ? "gift_view" : "b_view");
  }, [gift]);
  if (gift) {
    return (
      <div style={{ background: "#FFFDF9", minHeight: "100vh" }}>
        <style dangerouslySetInnerHTML={{ __html: styles }} />
        <div className="dp-wrap">
          <div className="dp-brand">
            «שאלת האריה»
            <small>סדנה בספר · שלמה אלפנבוים</small>
          </div>
          <div className="dp-gift-hero">
            <div className="dp-kicker">🎁 אין מתנה נפלאה יותר לחבר באמת קרוב</div>
            <h1>פיתוח אינטליגנציה רגשית זו מתנה לחיים</h1>
            <p>
              לבן/בת הזוג שנוטה להתפרץ, לילדים הבוגרים בתחילת הדרך, לחברה
              שתמיד אמרה "אני צריכה משהו כזה". 12 מפגשים בקולו של שלמה, בקצב
              שלהם, לתמיד - 89 ₪. ממלאים למי, משלמים בביט או בכרטיס, ותוך דקה
              המתנה אצלם במייל, בשמך ובמילים שלך.
            </p>
          </div>
        </div>
        <div id="dp-offer">
          <div onClickCapture={(e) => { const t = e.target as HTMLElement; if (t.closest("button")) logSiteEvent("gift_cta"); }}>
          <PaywallGate
            chapter={2}
            chapterTitle=""
            variant="direct"
            initialGift
            onBackToChapter1={() => {
              window.location.href = "/";
            }}
          />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#FFFDF9", minHeight: "100vh" }}>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="dp-wrap">
        <a className="dp-ribbon" href="/gift">
          <b>🎁 אין מתנה נפלאה יותר לחבר באמת קרוב</b>
          <span>פיתוח אינטליגנציה רגשית זו מתנה לחיים · 89 ₪ · לרכישה כמתנה ←</span>
        </a>
        <div className="dp-brand">
          «שאלת האריה»
          <small>סדנה בספר · שלמה אלפנבוים</small>
        </div>

        {/* Penetration vector (Shlomo, 31.8) — speaks to the skip reflex
            itself; feminine-singular adaptation of the ad copy. */}
        <div className="dp-vector">
          <h1>תהיי ממוקדת יותר, שקטה יותר, משמעותית יותר - ולמרות הכותרת הבומבסטית, את עומדת לסגור את הדף?</h1>
          <p>
            נכון, אנחנו לא מוכרים מוצרים מעלי-אקספרס. אנחנו מציעים דבר אחד:
            סדנה באינטליגנציה רגשית - בתוך ספר. אם המושג מוכר ומסקרן אותך -
            לא הייתה לך, וכנראה לא תהיה לך, הזדמנות ללמוד קורס משמעותי יותר
            לחיים שלך. <strong>כזה שאחריו אותם אנשים, אותם מצבים, אותו בוקר -
            מרגישים אחרת.</strong>
          </p>
          <button
            className="dp-cta"
            onClick={() => {
              logSiteEvent("b_cta");
              document.getElementById("dp-offer")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            אני רוצה להתחיל
            <small>כל הסדנה · 89 ₪ · פעם אחת, לתמיד</small>
          </button>
        </div>

        <div className="dp-quotes">
          <p>"לעצור את הרגשות השליליים שלי? איך? הם בכלל לא בגללי - זה מה שאחרים עושים ואיך שהם מתנהגים. איך אני קשורה לזה?"</p>
          <p>"לפעמים בא לי לחנוק אותו, כזה חוצפן. ואתה אומר לי שאני בוחרת להתרגז? באמת, אתה מגזים."</p>
          <p>"תוך שלוש שניות אני יכולה לשחרר את הכעס? זה לוקח לי שבוע."</p>
          <p>"אם אתה צודק אפילו בחצי מההבטחות שלך - אני רוצה ללמוד ממך. אני מפרקת כל זוגיות בגלל המזג שלי."</p>
        </div>

        <div className="dp-body">
          <p>
            אני שומע את המשפטים האלה כבר עשרים שנה. בכל סדנה, בכל קבוצה,
            מישהי אומרת אחד מהם - לפעמים את כולם. ותמיד אני עונה אותה תשובה:
          </p>
          <p>
            <strong>כן, את קשורה לזה.</strong> לא כי את אשמה - אלא כי הכעס
            נולד אצלך, לא אצלו. <strong>כן, את בוחרת</strong> - רק שהבחירה
            נעשית בשבילך, בשבריר שנייה, על ידי מערכת עתיקה שירשת ושעוד לא
            פגשת פנים אל פנים. <strong>כן, שלוש שניות.</strong>
          </p>
          <p>
            והנה מה שאני מבטיח לך - בזהירות של מי שראה את זה קורה מול העיניים
            אלפי פעמים: <strong>החיים שלך אחרי הסדנה הזאת ייראו אחרת.</strong>{" "}
            אותם אנשים, אותם מצבים, אותו חוצפן - ואת כבר לא נסחפת.
            <strong> החיים מקבלים בחזרה את הצבע.</strong> לא בעוד שנה של
            טיפול. זה מתחיל היום, בעמוד הזה.
          </p>
          {/* Shlomo's invitation line, 29.8 (verbatim, his register). */}
          <p className="dp-invite">
            אתם מוזמנים בפעם הראשונה לסדנה אינטראקטיבית (פעילה) באינטליגנציה
            רגשית בספר דיגיטלי, בסיומה תתחילו לראות את החיים אחרת -
            והתנהגותכם תשרה גם על סביבתכם שינויים מאוד מאוד חיוביים.
          </p>
        </div>
      </div>

      <div id="dp-offer">
        <PaywallGate
          chapter={2}
          chapterTitle=""
          variant="direct"
          onBackToChapter1={() => {
            window.location.href = "/";
          }}
        />
      </div>
    </div>
  );
}
